import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCity,
  FaFlag,
  FaBuilding,
  FaSave,
  FaTimes,
  FaUserCircle,
  FaArrowLeft,
  FaSpinner,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";

const EditProfile = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
  });

  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    // Simulate fetching user data from API
    const fetchUserData = () => {
      const userData = {
        fullName: "John Doe",
        email: "john.doe@example.com",
        phone: "+1 (555) 123-4567",
        address: "123 Shipping Lane",
        city: "Los Angeles",
        state: "California",
        country: "United States",
      };
      setFormData(userData);
      setOriginalData(userData);
    };

    fetchUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
    // Clear success message when user makes changes
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 3) {
      newErrors.fullName = "Full name must be at least 3 characters";
    } else if (formData.fullName.trim().length > 100) {
      newErrors.fullName = "Full name must not exceed 100 characters";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!formData.email.includes("@") || !formData.email.includes(".")) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state.trim()) {
      newErrors.state = "State/Province is required";
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      // Simulate API call
      setTimeout(() => {
        console.log("Profile updated:", formData);
        setOriginalData(formData);
        setSuccessMessage("Profile updated successfully!");
        setIsLoading(false);

        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      }, 1500);
    } else {
      setErrors(newErrors);
    }
  };

  const handleCancel = () => {
    navigate("/view-profile");
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const countries = [
    "Afghanistan",
    "Albania",
    "Algeria",
    "Andorra",
    "Angola",
    "Antigua and Barbuda",
    "Argentina",
    "Armenia",
    "Australia",
    "Austria",
    "Azerbaijan",
    "Bahamas",
    "Bahrain",
    "Bangladesh",
    "Barbados",
    "Belarus",
    "Belgium",
    "Belize",
    "Benin",
    "Bhutan",
    "Bolivia",
    "Bosnia and Herzegovina",
    "Botswana",
    "Brazil",
    "Brunei",
    "Bulgaria",
    "Burkina Faso",
    "Burundi",
    "Cabo Verde",
    "Cambodia",
    "Cameroon",
    "Canada",
    "Central African Republic",
    "Chad",
    "Chile",
    "China",
    "Colombia",
    "Comoros",
    "Congo",
    "Costa Rica",
    "Côte d'Ivoire",
    "Croatia",
    "Cuba",
    "Cyprus",
    "Czech Republic",
    "Denmark",
    "Djibouti",
    "Dominica",
    "Dominican Republic",
    "Ecuador",
    "Egypt",
    "El Salvador",
    "Equatorial Guinea",
    "Eritrea",
    "Estonia",
    "Eswatini",
    "Ethiopia",
    "Fiji",
    "Finland",
    "France",
    "Gabon",
    "Gambia",
    "Georgia",
    "Germany",
    "Ghana",
    "Greece",
    "Grenada",
    "Guatemala",
    "Guinea",
    "Guinea-Bissau",
    "Guyana",
    "Haiti",
    "Honduras",
    "Hungary",
    "Iceland",
    "India",
    "Indonesia",
    "Iran",
    "Iraq",
    "Ireland",
    "Israel",
    "Italy",
    "Jamaica",
    "Japan",
    "Jordan",
    "Kazakhstan",
    "Kenya",
    "Kiribati",
    "Korea, North",
    "Korea, South",
    "Kosovo",
    "Kuwait",
    "Kyrgyzstan",
    "Laos",
    "Latvia",
    "Lebanon",
    "Lesotho",
    "Liberia",
    "Libya",
    "Liechtenstein",
    "Lithuania",
    "Luxembourg",
    "Madagascar",
    "Malawi",
    "Malaysia",
    "Maldives",
    "Mali",
    "Malta",
    "Marshall Islands",
    "Mauritania",
    "Mauritius",
    "Mexico",
    "Micronesia",
    "Moldova",
    "Monaco",
    "Mongolia",
    "Montenegro",
    "Morocco",
    "Mozambique",
    "Myanmar",
    "Namibia",
    "Nauru",
    "Nepal",
    "Netherlands",
    "New Zealand",
    "Nicaragua",
    "Niger",
    "Nigeria",
    "North Macedonia",
    "Norway",
    "Oman",
    "Pakistan",
    "Palau",
    "Palestine",
    "Panama",
    "Papua New Guinea",
    "Paraguay",
    "Peru",
    "Philippines",
    "Poland",
    "Portugal",
    "Qatar",
    "Romania",
    "Russia",
    "Rwanda",
    "Saint Kitts and Nevis",
    "Saint Lucia",
    "Saint Vincent and the Grenadines",
    "Samoa",
    "San Marino",
    "Sao Tome and Principe",
    "Saudi Arabia",
    "Senegal",
    "Serbia",
    "Seychelles",
    "Sierra Leone",
    "Singapore",
    "Slovakia",
    "Slovenia",
    "Solomon Islands",
    "Somalia",
    "South Africa",
    "South Sudan",
    "Spain",
    "Sri Lanka",
    "Sudan",
    "Suriname",
    "Sweden",
    "Switzerland",
    "Syria",
    "Taiwan",
    "Tajikistan",
    "Tanzania",
    "Thailand",
    "Timor-Leste",
    "Togo",
    "Tonga",
    "Trinidad and Tobago",
    "Tunisia",
    "Turkey",
    "Turkmenistan",
    "Tuvalu",
    "Uganda",
    "Ukraine",
    "United Arab Emirates",
    "United Kingdom",
    "United States",
    "Uruguay",
    "Uzbekistan",
    "Vanuatu",
    "Vatican City",
    "Venezuela",
    "Vietnam",
    "Yemen",
    "Zambia",
    "Zimbabwe",
  ];

  return (
    <>
      <Helmet>
        <title>Edit Profile | Trans World Shipping</title>
        <meta
          name="description"
          content="Edit your Trans World Shipping account profile information."
        />
        <link
          rel="canonical"
          href="https://transworldshippingcompany.com/edit-profile"
        />
      </Helmet>

      <TheEditProfile>
        <div className="edit-container">
          <div className="edit-card">
            {/* Header */}
            <div className="edit-header">
              <Link to="/view-profile" className="back-link">
                <FaArrowLeft /> Back to Profile
              </Link>
              <div className="header-title">
                <FaUserCircle className="header-icon" />
                <div>
                  <h1>Edit Profile</h1>
                  <p>Update your personal information</p>
                </div>
              </div>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="success-alert">
                <span>✓</span> {successMessage}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h2>Personal Information</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fullName">
                      Full Name <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaUser className="input-icon" />
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className={errors.fullName ? "error" : ""}
                      />
                    </div>
                    {errors.fullName && (
                      <span className="error-message">{errors.fullName}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      Email Address <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaEnvelope className="input-icon" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className={errors.email ? "error" : ""}
                      />
                    </div>
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">
                      Phone Number <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaPhone className="input-icon" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 123-4567"
                        className={errors.phone ? "error" : ""}
                      />
                    </div>
                    {errors.phone && (
                      <span className="error-message">{errors.phone}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2>Address Information</h2>
                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="address">
                      Address <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaMapMarkerAlt className="input-icon" />
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Shipping Lane"
                        className={errors.address ? "error" : ""}
                      />
                    </div>
                    {errors.address && (
                      <span className="error-message">{errors.address}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">
                      City <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaCity className="input-icon" />
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Los Angeles"
                        className={errors.city ? "error" : ""}
                      />
                    </div>
                    {errors.city && (
                      <span className="error-message">{errors.city}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">
                      State/Province <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaBuilding className="input-icon" />
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="California"
                        className={errors.state ? "error" : ""}
                      />
                    </div>
                    {errors.state && (
                      <span className="error-message">{errors.state}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="country">
                      Country <span className="required">*</span>
                    </label>
                    <div className="input-wrapper">
                      <FaFlag className="input-icon" />
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className={errors.country ? "error" : ""}
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.country && (
                      <span className="error-message">{errors.country}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-btn"
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  className="save-btn"
                  disabled={isLoading || !hasChanges()}
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="spinning" /> Saving...
                    </>
                  ) : (
                    <>
                      <FaSave /> Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Info Note */}
            <div className="info-note">
              <p>
                <strong>Note:</strong> Your email address cannot be changed
                directly. Please contact support if you need to update your
                email.
              </p>
            </div>
          </div>
        </div>
      </TheEditProfile>
    </>
  );
};

const TheEditProfile = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9edf2 100%);
  padding: 2rem;

  .edit-container {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
  }

  .edit-card {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .edit-header {
    margin-bottom: 2rem;

    .back-link {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: #718096;
      text-decoration: none;
      font-size: 0.9rem;
      margin-bottom: 1.5rem;
      transition: color 0.3s ease;

      &:hover {
        color: #d4af37;
      }
    }

    .header-title {
      display: flex;
      align-items: center;
      gap: 1rem;

      .header-icon {
        font-size: 3rem;
        color: #d4af37;
      }

      h1 {
        font-size: 1.8rem;
        color: #0b1e33;
        margin-bottom: 0.25rem;
        font-weight: 700;
      }

      p {
        color: #718096;
        font-size: 0.9rem;
      }
    }
  }

  .success-alert {
    background: #f0fff4;
    border: 1px solid #4caf50;
    color: #2f855a;
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.9rem;

    span {
      font-size: 1.2rem;
      font-weight: bold;
    }
  }

  .form-section {
    margin-bottom: 2rem;

    h2 {
      font-size: 1.3rem;
      color: #0b1e33;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #e2e8f0;
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-group {
    &.full-width {
      grid-column: span 2;
    }

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #0b1e33;
      font-size: 0.9rem;

      .required {
        color: #e53e3e;
        margin-left: 2px;
      }
    }

    .input-wrapper {
      position: relative;

      .input-icon {
        position: absolute;
        left: 1rem;
        top: 50%;
        transform: translateY(-50%);
        color: #a0aec0;
        font-size: 1rem;
        pointer-events: none;
        z-index: 1;
      }

      input,
      select {
        width: 100%;
        padding: 0.85rem 1rem 0.85rem 2.75rem;
        border: 1.5px solid #e2e8f0;
        border-radius: 10px;
        font-size: 1rem;
        transition: all 0.3s ease;
        font-family: inherit;
        background: white;

        &:focus {
          outline: none;
          border-color: #d4af37;
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }

        &.error {
          border-color: #e53e3e;

          &:focus {
            box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.1);
          }
        }

        &:disabled {
          background: #f7fafc;
          cursor: not-allowed;
        }
      }

      select {
        cursor: pointer;
        appearance: none;
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23a0aec0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>');
        background-repeat: no-repeat;
        background-position: right 1rem center;
        padding-right: 2.5rem;
      }
    }

    .error-message {
      display: block;
      margin-top: 0.5rem;
      font-size: 0.8rem;
      color: #e53e3e;
    }
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;

    button {
      padding: 0.75rem 1.5rem;
      border-radius: 10px;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.3s ease;
      font-family: inherit;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .cancel-btn {
      background: #e2e8f0;
      color: #4a5568;
      border: none;

      &:hover:not(:disabled) {
        background: #cbd5e0;
        transform: translateY(-2px);
      }
    }

    .save-btn {
      background: #d4af37;
      color: #0b1e33;
      border: none;

      &:hover:not(:disabled) {
        background: #c4a12e;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
      }
    }
  }

  .info-note {
    margin-top: 1.5rem;
    padding: 1rem;
    background: #fef3c7;
    border-radius: 10px;
    border-left: 3px solid #f59e0b;

    p {
      font-size: 0.85rem;
      color: #92400e;
      line-height: 1.5;

      strong {
        font-weight: 700;
      }
    }
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media screen and (max-width: 768px) {
    padding: 1rem;

    .edit-card {
      padding: 1.5rem;
    }

    .edit-header .header-title h1 {
      font-size: 1.5rem;
    }

    .form-row {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .form-group.full-width {
      grid-column: span 1;
    }

    .form-actions {
      flex-direction: column-reverse;

      button {
        width: 100%;
        justify-content: center;
      }
    }
  }

  @media screen and (max-width: 480px) {
    .edit-card {
      padding: 1rem;
    }

    .edit-header .header-title {
      flex-direction: column;
      text-align: center;

      h1 {
        font-size: 1.3rem;
      }
    }

    .form-section h2 {
      font-size: 1.1rem;
    }
  }
`;

export default EditProfile;
